import type {
	UserSetting,
	UserSettingFavorites,
	UserSettingLanguage,
	UserSettingRecentVisited,
	UserSettingSearchTerms,
} from '@gov-nx/api/types/userSettings';
import { CommonSettingKey } from '@gov-nx/api/types/userSettings';
import type {
	SearchAllActionParams,
	SearchAllRecentVisited,
	SearchAllSearchTerms,
} from '@gov-nx/module/page/vysledky-vyhledavani/context.types';
import { SearchAllResultType } from '@gov-nx/module/page/vysledky-vyhledavani/context.types';

export const isLanguageSetting = (
	setting: UserSetting
): setting is UserSettingLanguage => setting.key === CommonSettingKey.Language;

export const isFavoritesSetting = (
	setting: UserSetting
): setting is UserSettingFavorites =>
	setting.key === CommonSettingKey.Favorites;

export const isSearchTermsSetting = (
	setting: UserSetting
): setting is UserSettingSearchTerms =>
	setting.key === CommonSettingKey.SearchTerms;

export const isRecentVisitedSetting = (
	setting: UserSetting
): setting is UserSettingRecentVisited =>
	setting.key === CommonSettingKey.RecentVisited;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const isString = (value: unknown): value is string => typeof value === 'string';

const asNonEmptyString = (value: unknown): string | undefined => {
	if (!isString(value)) {
		return;
	}

	const trimmed = value.trim();

	return trimmed.length ? trimmed : undefined;
};

const compactMap = <Input, Output>(
	items: Input[],
	mapper: (item: Input) => Output | undefined
): Output[] => items.map(mapper).filter((item): item is Output => !!item);

let searchResultTypeSet: Set<unknown> | undefined;

const getSearchResultTypeSet = () => {
	if (!searchResultTypeSet) {
		searchResultTypeSet = new Set<unknown>(
			Object.values(SearchAllResultType ?? {})
		);
	}

	return searchResultTypeSet;
};

const isSearchResultType = (value: unknown): value is SearchAllResultType =>
	getSearchResultTypeSet().has(value);

const sanitizeActionParams = (
	actionParams: unknown
): SearchAllActionParams | undefined => {
	if (!isRecord(actionParams)) {
		return;
	}

	const toId = (value: unknown): number | undefined => {
		if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
			return value;
		}

		if (typeof value === 'string' && /^\d+$/.test(value)) {
			const resolved = Number(value);

			return resolved > 0 ? resolved : undefined;
		}

		return;
	};

	const externalServiceId =
		toId(actionParams.externalServiceId) ?? toId(actionParams.externalLinkId);

	const sanitized = {
		pageCode: asNonEmptyString(actionParams.pageCode),
		serviceCode: asNonEmptyString(actionParams.serviceCode),
		externalServiceId,
		url: asNonEmptyString(actionParams.url),
		faqSlug: asNonEmptyString(actionParams.faqSlug),
	};

	const hasAnyValue = Object.values(sanitized).some(
		(value) => value !== undefined
	);

	return hasAnyValue ? sanitized : undefined;
};

export const sanitizeSearchTerms = (value: unknown): SearchAllSearchTerms[] => {
	if (!Array.isArray(value)) {
		return [];
	}

	const parseSearchTermEntry = (
		item: unknown
	): SearchAllSearchTerms | undefined => {
		if (isString(item)) {
			const searchTerm = asNonEmptyString(item);

			if (searchTerm) {
				return { rezaKod: '', searchTerm };
			}
		}

		if (!isRecord(item) || !isString(item.searchTerm)) {
			return;
		}

		const searchTerm = asNonEmptyString(item.searchTerm);
		if (!searchTerm) {
			return;
		}

		const rezaKod = isString(item.rezaKod) ? item.rezaKod.trim() : '';

		return {
			rezaKod,
			searchTerm,
		};
	};

	return compactMap(value, parseSearchTermEntry);
};

export const sanitizeRecentVisited = (
	value: unknown
): SearchAllRecentVisited[] => {
	if (!Array.isArray(value)) {
		return [];
	}

	const parseRecentVisitedEntry = (
		item: unknown
	): SearchAllRecentVisited | undefined => {
		if (!isRecord(item)) {
			return;
		}

		const title = asNonEmptyString(item.title);
		if (!title) {
			return;
		}

		if (!isSearchResultType(item.type)) {
			return;
		}

		const actionParams = sanitizeActionParams(item.actionParams);

		if (!actionParams) {
			return;
		}

		return {
			rezaKod: isString(item.rezaKod) ? item.rezaKod.trim() : '',
			title,
			type: item.type,
			actionParams,
		};
	};

	return compactMap(value, parseRecentVisitedEntry);
};
